import { Outlet, createFileRoute, useRouter } from '@tanstack/react-router';
import { useEffect } from 'react';
import { AppHeader } from '@/components/layout/app-header';
import { RouteErrorBoundary } from '@/components/shared/RouteErrorBoundary';
import { isUnauthenticated, redirectToSignIn, useSession } from '@/features/auth/session';
import { useQueryClient } from '@tanstack/react-query';
import { scheduleSessionExpiry } from '@/lib/session-expiry';
import { homeVisibility } from '@/features/home/home-model';
import { ProductProvider, useProduct } from '@/features/home/product-context';
import { useProductBackground } from '@/features/home/use-product-background';
import { useBackButton } from '@/lib/device/back-button';

function AppErrorBoundary({ error }: { error: unknown }) {
  const router = useRouter();
  return (
    <RouteErrorBoundary
      error={error}
      boundary="app-shell"
      onRetry={() => void router.invalidate()}
    />
  );
}

/** Tints the whole shell for the selected product; must sit inside ProductProvider. */
function ProductBackground(): null {
  useProductBackground(useProduct().product);
  return null;
}

function AppLayout(): React.JSX.Element | null {
  const session = useSession();
  const queryClient = useQueryClient();

  // unified-access.md section 3.1 (SH-28): once the time left that the server
  // reported has passed and no save is in flight, ask the server again. Only
  // its 401 ends the session (the effect below then returns to Unified Access
  // with this page); a still-valid answer re-arms the timer. The device clock
  // is never compared with the server's expiry time.
  const { refetch, dataUpdatedAt } = session;
  const expiresInMs = session.data?.expiresInMs;
  useEffect(
    () =>
      scheduleSessionExpiry(
        expiresInMs,
        dataUpdatedAt,
        () => void refetch(),
        () => queryClient.isMutating() > 0,
      ),
    [expiresInMs, dataUpdatedAt, refetch, queryClient],
  );
  useBackButton();

  useEffect(() => {
    // unified-access.md section 3, step 5: no session sends the browser to
    // Unified Access with a return address.
    if (!session.isPending && isUnauthenticated(session.error)) {
      redirectToSignIn();
    }
  }, [session.isPending, session.error]);

  if (session.isPending) return null;
  if (isUnauthenticated(session.error)) return null;
  if (session.error) throw session.error;

  return (
    <ProductProvider allowed={homeVisibility(session.data.services).products}>
      <ProductBackground />
      <div className="mx-auto flex min-h-svh w-full max-w-[480px] flex-col bg-background">
        <AppHeader session={session.data} />
        <main className="flex flex-1 flex-col gap-4 p-4">
          <Outlet />
        </main>
      </div>
    </ProductProvider>
  );
}

/** Everything under this layout requires a session. */
export const Route = createFileRoute('/_app')({
  component: AppLayout,
  errorComponent: AppErrorBoundary,
});
